import { ComponentProps, ElementType, FC } from 'react';

import { cva } from 'class-variance-authority';

import { cn } from '@/utils/tailwind';

type IconBoxProps = ComponentProps<'div'> & {
  variant?: 'warning' | 'danger' | 'default' | 'default-cyan' | 'default-gray';
  size?: 'small' | 'regular' | 'large';
  isSelected?: boolean;
  icon: ElementType;
  className?: string;
  iconClassName?: string;
};

const iconBoxVariants = cva('flex items-center justify-center rounded-[8px]', {
  variants: {
    variant: {
      default: 'bg-custom-warm-grey-100',
      'default-cyan': 'text-custom-cyan-500 bg-custom-warm-grey-100',
      'default-gray': 'text-icons-secondary bg-custom-warm-grey-100',
      danger: 'bg-custom-red-100',
      warning: 'bg-custom-orange-100',
    },
    size: {
      small: 'size-[32px]',
      regular: 'size-[40px]',
      large: 'size-[52px]',
    },
    isSelected: {
      true: 'bg-custom-cyan-100 text-custom-cyan-500',
      false: '',
    },
  },
  compoundVariants: [
    {
      variant: 'danger',
      class: 'size-[40px]',
    },
    {
      variant: 'warning',
      class: 'size-[40px]',
    },
    {
      isSelected: true,
      variant: 'default',
      class: 'bg-custom-cyan-100 !bg-none',
    },
    {
      isSelected: true,
      variant: 'danger',
      class: 'bg-custom-cyan-100 !bg-none',
    },
    {
      isSelected: true,
      variant: 'warning',
      class: 'bg-custom-cyan-100 !bg-none',
    },
    {
      isSelected: true,
      variant: 'default-cyan',
      class: 'bg-custom-cyan-100 !bg-none text-custom-cyan-500',
    },
    {
      isSelected: true,
      variant: 'default-gray',
      class: 'bg-custom-cyan-100 !bg-none text-icons-secondary',
    },
    {
      isSelected: true,
      variant: 'default-cyan',
      class: 'bg-custom-cyan-100 !bg-none text-custom-cyan-500',
    },
    {
      isSelected: true,
      variant: 'default-gray',
      class: 'bg-custom-cyan-100 !bg-none text-icons-secondary',
    },
  ],
  defaultVariants: {
    variant: 'default',
    size: 'regular',
    isSelected: false,
  },
});

const iconSizeVariants = cva('', {
  variants: {
    variant: {
      default: '',
      'default-cyan': '',
      'default-gray': '',
      danger: 'size-8',
      warning: 'size-8',
    },
    size: {
      small: 'size-5',
      regular: 'size-5',
      large: 'size-6',
    },
  },
  compoundVariants: [
    {
      variant: 'default',
      size: 'regular',
      class: 'size-5',
    },
    {
      variant: 'default',
      size: 'small',
      class: 'size-5',
    },
    {
      variant: 'default',
      size: 'large',
      class: 'size-6',
    },
    {
      variant: 'default-cyan',
      size: 'regular',
      class: 'size-5',
    },
    {
      variant: 'default-cyan',
      size: 'small',
      class: 'size-5',
    },
    {
      variant: 'default-cyan',
      size: 'large',
      class: 'size-6',
    },
    {
      variant: 'default-gray',
      size: 'regular',
      class: 'size-5',
    },
    {
      variant: 'default-gray',
      size: 'small',
      class: 'size-5',
    },
    {
      variant: 'default-gray',
      size: 'large',
      class: 'size-6',
    },
    {
      variant: 'default-cyan',
      size: 'regular',
      class: 'size-5',
    },
    {
      variant: 'default-cyan',
      size: 'small',
      class: 'size-5',
    },
    {
      variant: 'default-cyan',
      size: 'large',
      class: 'size-6',
    },
    {
      variant: 'default-gray',
      size: 'regular',
      class: 'size-5',
    },
    {
      variant: 'default-gray',
      size: 'small',
      class: 'size-5',
    },
    {
      variant: 'default-gray',
      size: 'large',
      class: 'size-6',
    },
    {
      variant: 'danger',
      class: 'size-[20px] text-custom-red-700',
    },
    {
      variant: 'warning',
      class: 'size-[20px] text-custom-orange-700',
    },
  ],
});

export const IconBox: FC<IconBoxProps> = ({
  variant = 'default',
  icon: Icon,
  size = 'regular',
  isSelected,
  className,
  iconClassName,
  ...props
}) => {
  return (
    <div {...props} className={cn(iconBoxVariants({ variant, size, isSelected }), className)}>
      <Icon className={cn(iconSizeVariants({ variant, size, className: iconClassName }))} />
    </div>
  );
};
